import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle, MapPin, ShieldCheck, Sparkles } from "lucide-react";
import { BRAND } from "../lib/constants";

const HERO_IMG =
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=80";

export default function Hero() {
    return (
        <section
            id="top"
            className="relative min-h-[100svh] w-full overflow-hidden bg-brand-ink"
            data-testid="hero-section"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Luxury modern home built by Dhanshree Constructions, the best construction company in Udaipur"
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchpriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/85" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
            </div>

            {/* Decorative gradient orb */}
            <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[#FF8800] opacity-20 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-[#CB1010] opacity-15 blur-[140px] pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pt-28 md:pt-36 pb-28">
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-2 glass-dark rounded-full px-3.5 py-1.5 mb-6 text-white/90 text-[11px]"
                            data-testid="hero-badge"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF8800] animate-pulse" />
                            <MapPin size={11} className="opacity-80" />
                            Best Construction Company in Udaipur, Rajasthan
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                            className="font-display font-semibold text-white text-[38px] sm:text-[52px] lg:text-[68px] xl:text-[76px] leading-[1.05] tracking-[-0.03em]"
                            data-testid="hero-headline"
                        >
                            Turning Dreams
                            <br className="hidden sm:block" />{" "}
                            Into <span className="font-serif-italic font-normal brand-gradient-text">Concrete</span> Reality.
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.18 }}
                            className="mt-6 max-w-xl text-white/75 text-[15px] md:text-base leading-relaxed"
                            data-testid="hero-subheadline"
                        >
                            Udaipur's trusted end-to-end home builder & luxury interior
                            specialist — from architectural planning to housewarming, delivered
                            with craftsmanship and care.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.32 }}
                            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md sm:max-w-none"
                        >
                            <a
                                href={BRAND.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center justify-center gap-2.5 brand-gradient text-white px-6 py-4 rounded-full font-medium text-[15px] hover:opacity-95 transition w-full sm:w-auto"
                                data-testid="hero-book-site-visit-btn"
                            >
                                <span>Book Free Site Visit</span>
                                <ArrowUpRight size={17} strokeWidth={2.4} className="group-hover:rotate-45 transition-transform duration-300" />
                            </a>
                            <a
                                href={BRAND.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md text-white border border-white/25 hover:bg-white hover:text-brand-ink px-6 py-4 rounded-full font-medium text-[15px] transition w-full sm:w-auto"
                                data-testid="hero-whatsapp-btn"
                            >
                                <MessageCircle size={16} />
                                Chat on WhatsApp
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                            className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-white/65 text-[10px] uppercase tracking-[0.18em]"
                            data-testid="hero-trust-row"
                        >
                            <span className="inline-flex items-center gap-2"><ShieldCheck size={13} className="text-[#FF8800]" /> Free Consultation</span>
                            <span className="w-1 h-1 rounded-full bg-white/30" />
                            <span className="inline-flex items-center gap-2"><Sparkles size={13} className="text-[#FF8800]" /> Site Inspection</span>
                            <span className="w-1 h-1 rounded-full bg-white/30" />
                            <span className="inline-flex items-center gap-2"><MapPin size={13} className="text-[#FF8800]" /> Udaipur Only</span>
                        </motion.div>
                    </div>

                    {/* Floating UI card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.4 }}
                        className="lg:col-span-4 hidden lg:block"
                    >
                        <div className="glass-dark rounded-2xl p-5 text-white">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 mb-3">Live Project</div>
                            <div className="font-display font-semibold text-lg leading-tight mb-1">Villa Aravalli — Phase 3</div>
                            <div className="text-white/55 text-xs">Sukher, Udaipur</div>

                            <div className="mt-5 space-y-3">
                                {[
                                    { label: "Foundation", v: 100 },
                                    { label: "Structure", v: 92 },
                                    { label: "Finishing", v: 64 },
                                ].map((i) => (
                                    <div key={i.label}>
                                        <div className="flex justify-between text-[11px] text-white/65 mb-1">
                                            <span>{i.label}</span>
                                            <span className="tabular-nums text-white">{i.v}%</span>
                                        </div>
                                        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${i.v}%` }}
                                                transition={{ duration: 1.2, delay: 0.7, ease: "easeOut" }}
                                                className="h-full brand-gradient"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-[11px]">
                                <div>
                                    <div className="text-white/45 mb-0.5">Daily site updates</div>
                                    <div className="text-white">Sent at 6:30 PM IST</div>
                                </div>
                                <div className="w-8 h-8 rounded-full brand-gradient flex items-center justify-center">
                                    <ArrowUpRight size={12} strokeWidth={2.4} className="text-white" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Bottom marquee */}
            <div className="absolute bottom-0 inset-x-0 z-10 overflow-hidden border-t border-white/10 backdrop-blur-md bg-black/30">
                <div className="flex marquee-track py-3 whitespace-nowrap">
                    {[...Array(2)].map((_, k) => (
                        <div key={k} className="flex items-center gap-10 pr-10 text-white/55 text-[10px] uppercase tracking-[0.3em] font-medium">
                            <span>Residential Construction Udaipur</span>
                            <span className="text-[#FF8800]">◆</span>
                            <span>Luxury Home Builder</span>
                            <span className="text-[#FF8800]">◆</span>
                            <span>Vastu Architecture</span>
                            <span className="text-[#FF8800]">◆</span>
                            <span>Turnkey Solutions</span>
                            <span className="text-[#FF8800]">◆</span>
                            <span>Modular Interiors</span>
                            <span className="text-[#FF8800]">◆</span>
                            <span>3D Elevation Design</span>
                            <span className="text-[#FF8800]">◆</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
